<template>
  <div
    class="psui-el-accordion-item"
    :class="{ 'status-opened': isOpen, disabled: disabled }"
  >
    <div class="psui-el-accordion-item-header">
      <slot
        name="custom-header"
        :toggle="toggle"
        :is-open="isOpen"
      >
        <div
          @click="toggle"
          @keydown.enter.stop="toggle"
          @focusin="onHeaderFocusIn"
          @focusout="onHeaderFocusOut"
          class="psui-el-accordion-item-header-wrapper"
          tabindex="0"
          v-if="!hasCustomHeader"
        >
          <h2
            v-if="title"
            class="psui-el-accordion-item-title"
          >
            {{ title }}
          </h2>
          <slot name="header-additionals" />
          <i
            v-if="!disabled"
            class="psui-el-accordion-item-icon"
          >{{ getIcon }}</i>
        </div>
      </slot>
    </div>
    <transition
      name="accordion-fade"
      :css="false"
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
    >
      <div
        v-if="isOpen"
        class="psui-el-accordion-item-content"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { getParentVueComponentByName } from '../../util/GeneralFunctions.js'
import { useCollapseAnimation } from '../../../dist/composables/useCollapseAnimation.js'
import { ref, computed, getCurrentInstance, onBeforeUnmount  } from 'vue'

const { beforeEnter, enter, afterEnter, beforeLeave, leave } = useCollapseAnimation()

const emit = defineEmits(['header-focusin', 'header-focusout'])

defineExpose({
  toggle: () => _toggle()
})

const localOpened = ref(null)

const props = defineProps({
  /**
   * It sets the title of the accordion item.
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * It sets the status of the accordion item when mounted.
   */
  opened: {
    type: [Boolean],
    default: false,
  },
  /**
   * It sets the text key to get the svg icon in Google Fonts. Make sure to get the correct description of your icon on https://fonts.google.com/.
   */
  icon: {
    type: String,
    default: '',
  },
  /**
   * It sets if it has a custom header.
   */
  hasCustomHeader: {
    type: Boolean,
    default: false,
  },
  /**
   * It sets the disabled status.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * A callback called before toggling. If it returns false, the toggle is prevented.
   * Only affects user-initiated interactions (click/Enter), not programmatic calls.
   */
  beforeToggle: {
    type: Function,
    default: null,
  },
})

const isOpen = computed(() => {
  return localOpened.value !== null ? localOpened.value : props.opened
})

const getParent = computed(() => {
  const instance = getCurrentInstance()

  if (instance?.parent) {
    return getParentVueComponentByName(instance.parent, 'PsAccordion')
  }

  if (instance.proxy && instance.proxy.$parent) {
    return getParentVueComponentByName(instance.proxy.$parent, 'PsAccordion')
  }

  return null
})

const getIcon = computed(() => {
  if (!getParent.value) return

  const layout = getParent.value._props ? getParent.value._props.layout : getParent?.value?.props.layout.default
  if (layout === 'medium') return 'expand_more'
  if (layout === 'big' && isOpen.value === false) return 'add'
  if (layout === 'big' && isOpen.value === true) return 'remove'
  return `${props.icon}`
})

const _toggle = () => {
  if (localOpened.value === null) {
    localOpened.value = !props.opened
  } else {
    localOpened.value = !localOpened.value
  }
}

const toggle = () => {
  if (props.disabled) return
  if (props.beforeToggle && props.beforeToggle() === false) return
  _toggle()
}

let focusOutTimer = null
let suppressFocusIn = false

onBeforeUnmount(() => {
  clearTimeout(focusOutTimer)
})

const onHeaderFocusIn = (e) => {
  if (suppressFocusIn) return
  if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return
  if (focusOutTimer) {
    clearTimeout(focusOutTimer)
    focusOutTimer = null
    return
  }
  emit('header-focusin', e)
}

const onHeaderFocusOut = (e) => {
  if (!e.currentTarget.contains(e.relatedTarget)) {
    const wrapper = e.currentTarget
    clearTimeout(focusOutTimer)
    focusOutTimer = setTimeout(() => {
      focusOutTimer = null
      const activeEl = document.activeElement
      if (!wrapper.contains(activeEl)) {
        suppressFocusIn = true
        emit('header-focusout', e)
        if (activeEl && document.activeElement !== activeEl && activeEl.isConnected) {
          activeEl.focus()
        }
        suppressFocusIn = false
      }
    }, 0)
  }
}

</script>

<style>
/* Please, use the file src/assets/scss/components/PsAccordion.scss */
</style>



